"use client";

import { useCallback, useRef, useState } from "react";
import { trpc } from "../trpc";
import { useSupportedAssets } from "../folder/useSupportedAssets";
import { useUserAccountRole } from "./useUserAccountRole";
import {
  buildZapFolderPermissionsRequest,
  getDeletableZapIds,
  type ZapDeleteMetadata,
} from "./zap/getDeletableZapIds";

interface Result {
  deletableIds: string[];
  nonDeletableIds: string[];
  isLoading: boolean;
  hasChecked: boolean;
}

interface CheckOptions {
  /** Zaps without metadata here are assumed deletable. */
  zaps?: ZapDeleteMetadata[];
}

// Permissions API allows max 30 checks per request (asset_ids × permission_levels).
// We send one permission level, so cap at 25 IDs per request for safety margin.
const PERMISSION_BATCH_SIZE = 25;

// Typed-id prefixes whose permission asset type differs from the prefix.
const PERMISSION_ASSET_TYPE_BY_PREFIX: Record<string, string> = {
  tableView: "table_view",
};

/**
 * Checks can_delete permissions for a batch of type-prefixed asset IDs
 * (e.g. "zap:123", "table:456", "folder:abc"). Groups by asset type
 * and makes one permission check per type. Folders are assumed deletable
 * since there's no granular delete permission for them today.
 *
 * Zaps are not on FGA: pass their metadata in `options.zaps` to apply the Zap
 * kebab's delete rule. Without metadata they are assumed deletable.
 *
 * Call `check(ids)` explicitly when the permission check should run (e.g.
 * when the delete modal opens, or on selection change for the toolbar).
 */
export function useBulkCheckDeletePermissions(): Result & {
  check: (ids: string[], options?: CheckOptions) => Promise<void>;
} {
  const utils = trpc.useUtils();
  const { customuserId, isAccountOwner, isSuperAdmin } = useUserAccountRole();
  const { isMPFEnabled } = useSupportedAssets();
  const requestIdReference = useRef(0);
  const [result, setResult] = useState<Result>({
    deletableIds: [],
    nonDeletableIds: [],
    isLoading: false,
    hasChecked: false,
  });

  const getFolderCanDelete = useCallback(
    async (folderId: string) => {
      try {
        const data = await utils.permission.checkPermissions.ensureData(
          buildZapFolderPermissionsRequest(folderId),
        );
        const permissionLevels = data?.results?.[0]?.permissionLevels as
          | Record<string, boolean | undefined>
          | undefined;
        return !!permissionLevels?.canDelete;
      } catch {
        // Fail closed: treat an unknown folder permission as "can't delete".
        return;
      }
    },
    [utils.permission.checkPermissions],
  );

  const getFolderIsShared = useCallback(
    async (folderId: string) => {
      try {
        const tree = await utils.folder.folderTree.ensureData({});
        return !!tree?.hash?.[folderId]?.isShared;
      } catch {
        return false;
      }
    },
    [utils.folder.folderTree],
  );

  const check = useCallback(
    async (ids: string[], options?: CheckOptions) => {
      const requestId = ++requestIdReference.current;
      if (ids.length === 0) {
        setResult({
          deletableIds: [],
          nonDeletableIds: [],
          isLoading: false,
          hasChecked: true,
        });
        return;
      }

      setResult({
        deletableIds: [],
        nonDeletableIds: [],
        isLoading: true,
        hasChecked: false,
      });

      const grouped: Record<string, { rawId: string; typedId: string }[]> = {};
      for (const typedId of ids) {
        const colonIndex = typedId.indexOf(":");
        const type = typedId.slice(0, colonIndex);
        const rawId = typedId.slice(colonIndex + 1);
        if (!grouped[type]) grouped[type] = [];
        grouped[type].push({ rawId, typedId });
      }

      const deletable: string[] = [];

      // Folders have no granular delete permission. Next Gen Zaps aren't a
      // supported permissionAssetTypeSchema value, so they skip the FGA check
      // the same way. Both are assumed deletable. Classic Zaps are resolved
      // below from their metadata.
      const skipFgaCheck = new Set(["folder", "nextGenZap", "zap"]);

      const typesToCheck = Object.entries(grouped).filter(
        ([type]) => !skipFgaCheck.has(type),
      );

      for (const type of ["folder", "nextGenZap"]) {
        if (grouped[type]) {
          for (const item of grouped[type]) {
            deletable.push(item.typedId);
          }
        }
      }

      const zapItems = grouped.zap ?? [];
      const zapMetadataById = new Map(
        (options?.zaps ?? []).map((zap) => [String(zap.id), zap]),
      );
      const zapsWithMetadata = zapItems.filter((item) =>
        zapMetadataById.has(item.rawId),
      );
      for (const item of zapItems) {
        if (!zapMetadataById.has(item.rawId)) {
          deletable.push(item.typedId);
        }
      }

      const batches = typesToCheck.flatMap(([assetType, items]) => {
        const chunks: { rawId: string; typedId: string }[][] = [];
        for (
          let index = 0;
          index < items.length;
          index += PERMISSION_BATCH_SIZE
        ) {
          chunks.push(items.slice(index, index + PERMISSION_BATCH_SIZE));
        }
        return chunks.map((chunk) => ({ assetType, chunk }));
      });

      const checkZaps = async () => {
        if (zapsWithMetadata.length === 0) return;
        const deletableZapIds = await getDeletableZapIds({
          zaps: zapsWithMetadata.map(
            (item) => zapMetadataById.get(item.rawId) as ZapDeleteMetadata,
          ),
          customuserId,
          isAccountOwner,
          isSuperAdmin,
          isMPFEnabled,
          getFolderCanDelete,
          getFolderIsShared,
        });
        const deletableZapIdSet = new Set(deletableZapIds.map(String));
        for (const item of zapsWithMetadata) {
          if (deletableZapIdSet.has(item.rawId)) {
            deletable.push(item.typedId);
          }
        }
      };

      // Each batch gets its own try/catch so one failing asset type doesn't
      // block the rest; a failed batch is treated as non-deletable (fail-closed).
      await Promise.all([
        checkZaps(),
        ...batches.map(async ({ assetType, chunk }) => {
          try {
            const data = await utils.permission.checkPermissions.fetch({
              assetIds: chunk.map((item) => item.rawId),
              assetType: (PERMISSION_ASSET_TYPE_BY_PREFIX[assetType] ??
                assetType) as any,
              permissionLevels: ["can_delete"],
            });
            if (!data?.results) return;
            const permMap = new Map<string, boolean>();
            for (const r of data.results) {
              permMap.set(
                String(r.assetId),
                !!(r.permissionLevels as Record<string, boolean>)?.canDelete,
              );
            }
            for (const item of chunk) {
              if (permMap.get(item.rawId)) {
                deletable.push(item.typedId);
              }
            }
          } catch {
            // Permission check failed for this batch. Don't assume deletable --
            // the user will see these assets as non-deletable in the modal.
          }
        }),
      ]);

      if (requestId !== requestIdReference.current) return;

      const deletableSet = new Set(deletable);
      const nonDeletableIds = ids.filter((id) => !deletableSet.has(id));

      setResult({
        deletableIds: deletable,
        nonDeletableIds,
        isLoading: false,
        hasChecked: true,
      });
    },
    [
      customuserId,
      getFolderCanDelete,
      getFolderIsShared,
      isAccountOwner,
      isMPFEnabled,
      isSuperAdmin,
      utils.permission.checkPermissions,
    ],
  );

  return { ...result, check };
}
